import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import posthog from 'posthog-js';

export default function Signup() {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (firstName.trim().length === 0) {
      setError('Please enter your first name.');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/signup', {
        first_name: firstName,
        email,
        password,
      });
      const jwt = res.data.access_token;
      localStorage.setItem('token', jwt);
      try {
        const meRes = await api.get('/auth/me');
        login(meRes.data, jwt);
        posthog.identify(meRes.data.id, {
          email: meRes.data.email,
          first_name: meRes.data.first_name,
        });
        posthog.capture('signup_completed');
        navigate('/onboarding/welcome');
      } catch {
        login(null, jwt);
        navigate('/onboarding/welcome');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not create account. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.logo}>
          <span style={styles.logoTru}>Tru</span>
          <span style={styles.logoFlow}>Flow</span>
        </h1>

        <form onSubmit={handleSubmit} noValidate>
          <div style={styles.field}>
            <label htmlFor="firstName" style={styles.label}>First name</label>
            <input
              id="firstName"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              style={styles.input}
              placeholder="Jane"
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="email" style={styles.label}>Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={styles.input}
              placeholder="you@example.com"
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="password" style={styles.label}>Password</label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={styles.input}
              placeholder="••••••••"
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="confirmPassword" style={styles.label}>Confirm password</label>
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              style={styles.input}
              placeholder="••••••••"
            />
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" disabled={loading} style={styles.button}>
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p style={styles.signinText}>
          Already have an account?{' '}
          <Link to="/login" style={styles.signinLink}>Sign in</Link>
        </p>
      </div>
      <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: 'var(--mute)' }}>
        <a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=cd80ded6-7249-43fa-935c-0093b03f9dde"
           target="_blank" rel="noopener noreferrer" style={{ color: 'var(--mute)', marginRight: 12 }}>
          Privacy Policy
        </a>
        <a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=479a6457-8426-4afa-a7b1-f879c691825d"
           target="_blank" rel="noopener noreferrer" style={{ color: 'var(--mute)' }}>
          Terms of Service
        </a>
        <button
          className="termly-display-preferences"
          onClick={() => { if (window.displayPreferenceModal) window.displayPreferenceModal(); }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--mute)',
            fontSize: 11,
            marginLeft: 12,
            padding: 0,
            textDecoration: 'underline'
          }}
        >
          Consent Preferences
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: 'var(--paper)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    boxSizing: 'border-box',
  },
  card: {
    backgroundColor: 'var(--card)',
    borderRadius: 'var(--r-card)',
    boxShadow: 'var(--sh-raise)',
    padding: '40px 36px',
    width: '100%',
    maxWidth: '400px',
    boxSizing: 'border-box',
  },
  logo: {
    fontFamily: 'var(--font-serif)',
    fontSize: '2rem',
    fontWeight: '700',
    textAlign: 'center',
    margin: '0 0 32px 0',
    letterSpacing: '-0.5px',
  },
  logoTru: {
    color: 'var(--ink)',
  },
  logoFlow: {
    color: 'var(--signal)',
  },
  field: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: 'var(--slate)',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '1rem',
    border: '1px solid var(--input-border)',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
    color: 'var(--ink)',
    backgroundColor: 'var(--card)',
  },
  error: {
    color: 'var(--stop)',
    fontSize: '0.875rem',
    margin: '0 0 16px 0',
    padding: '10px 12px',
    backgroundColor: 'var(--stop-soft)',
    border: '1px solid var(--stop-border)',
    borderRadius: '8px',
  },
  button: {
    width: '100%',
    padding: '11px',
    backgroundColor: 'var(--signal)',
    color: 'var(--on-signal)',
    fontSize: '1rem',
    fontWeight: '600',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'background-color 0.15s',
  },
  signinText: {
    textAlign: 'center',
    fontSize: '0.875rem',
    color: 'var(--mute)',
    marginTop: '24px',
    marginBottom: '0',
  },
  signinLink: {
    color: 'var(--signal)',
    textDecoration: 'none',
    fontWeight: '500',
  },
};
